import React, { useState } from 'react';
import taxApi, { TaxReport } from '../../api/tax';

const TaxReportsPage: React.FC = () => {
  const [report, setReport] = useState<TaxReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportType, setReportType] = useState<'custom' | 'quarterly' | 'annual'>('quarterly');

  // Custom date range
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Quarterly BAS
  const currentYear = new Date().getFullYear();
  const [basYear, setBasYear] = useState(currentYear);
  const [basQuarter, setBasQuarter] = useState(1);

  // Annual report
  const [financialYear, setFinancialYear] = useState(currentYear);

  const generateReport = async () => {
    try {
      setLoading(true);
      setError('');

      let data: TaxReport;

      if (reportType === 'custom') {
        if (!startDate || !endDate) {
          setError('Please select start and end dates');
          return;
        }
        data = await taxApi.getReport(startDate, endDate);
      } else if (reportType === 'quarterly') {
        data = await taxApi.getQuarterlyBAS(basYear, basQuarter);
      } else {
        data = await taxApi.getAnnualReport(financialYear);
      }

      setReport(data);
    } catch (err: any) {
      setError(err.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = async () => {
    try {
      if (!report) return;

      const csv = await taxApi.exportCSV(report.startDate, report.endDate);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tax-report-${report.startDate}-to-${report.endDate}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to export CSV');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(amount);
  };

  return (
    <div className="max-w-7xl mx-auto py-8">
        <h1 className="text-3xl font-bold mb-6" style={{ color: '#0C4432' }}>Tax &amp; BAS Reports</h1>

        {/* Report Type Selection */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Generate Report</h2>

          <div className="space-y-4">
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="quarterly"
                  checked={reportType === 'quarterly'}
                  onChange={(e) => setReportType(e.target.value as any)}
                  className="mr-2"
                />
                <span>Quarterly BAS</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="annual"
                  checked={reportType === 'annual'}
                  onChange={(e) => setReportType(e.target.value as any)}
                  className="mr-2"
                />
                <span>Annual Tax Report</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="custom"
                  checked={reportType === 'custom'}
                  onChange={(e) => setReportType(e.target.value as any)}
                  className="mr-2"
                />
                <span>Custom Date Range</span>
              </label>
            </div>

            {reportType === 'quarterly' && (
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                  <select
                    value={basYear}
                    onChange={(e) => setBasYear(parseInt(e.target.value))}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Quarter</label>
                  <select
                    value={basQuarter}
                    onChange={(e) => setBasQuarter(parseInt(e.target.value))}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value={1}>Q1 (Jan-Mar)</option>
                    <option value={2}>Q2 (Apr-Jun)</option>
                    <option value={3}>Q3 (Jul-Sep)</option>
                    <option value={4}>Q4 (Oct-Dec)</option>
                  </select>
                </div>
              </div>
            )}

            {reportType === 'annual' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Financial Year (ending June 30)
                </label>
                <select
                  value={financialYear}
                  onChange={(e) => setFinancialYear(parseInt(e.target.value))}
                  className="w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((year) => (
                    <option key={year} value={year}>
                      FY {year - 1}/{year}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Australian financial year runs from July 1 to June 30
                </p>
              </div>
            )}

            {reportType === 'custom' && (
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            <button
              onClick={generateReport}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2.5 rounded-10 text-[13px] font-semibold text-white bg-forest-900 hover:bg-forest-800 transition-colors shadow-sm disabled:opacity-50"
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Report Display */}
        {report && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {report.reportType === 'QUARTERLY_BAS' && 'Quarterly BAS Summary'}
                    {report.reportType === 'ANNUAL_TAX' && 'Annual Tax Report'}
                    {report.reportType !== 'QUARTERLY_BAS' && report.reportType !== 'ANNUAL_TAX' && 'Tax Report'}
                  </h2>
                  <p className="text-gray-600">
                    Period: {new Date(report.startDate).toLocaleDateString()} -{' '}
                    {new Date(report.endDate).toLocaleDateString()}
                  </p>
                  <p className="text-gray-600">Owner: {report.ownerName}</p>
                </div>
                <button
                  onClick={exportCSV}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-10 text-[13px] font-semibold text-white bg-forest-900 hover:bg-forest-800 transition-colors shadow-sm"
                >
                  Export CSV
                </button>
              </div>
            </div>

            {/* Income */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Income</h3>
              </div>
              <div className="p-6">
                <table className="w-full">
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="py-2 text-gray-700">Rental Income</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(report.totalRentIncome)}</td>
                    </tr>
                    <tr>
                      <td className="py-2 text-gray-700">Bond Income</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(report.totalBondIncome)}</td>
                    </tr>
                    <tr>
                      <td className="py-2 text-gray-700">Other Income</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(report.otherIncome)}</td>
                    </tr>
                    <tr className="bg-gray-50 font-bold">
                      <td className="py-2 text-gray-900">Total Income</td>
                      <td className="py-2 text-right text-green-600">{formatCurrency(report.totalIncome)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Expenses */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Deductible Expenses</h3>
              </div>
              <div className="p-6">
                <table className="w-full">
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="py-2 text-gray-700">Maintenance & Repairs</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(report.maintenanceExpenses)}</td>
                    </tr>
                    <tr>
                      <td className="py-2 text-gray-700">Agent Management Fees</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(report.agentFeesExpenses)}</td>
                    </tr>
                    <tr>
                      <td className="py-2 text-gray-700">Insurance</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(report.insuranceExpenses)}</td>
                    </tr>
                    <tr>
                      <td className="py-2 text-gray-700">Council Rates</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(report.councilRatesExpenses)}</td>
                    </tr>
                    <tr>
                      <td className="py-2 text-gray-700">Water Rates</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(report.waterRatesExpenses)}</td>
                    </tr>
                    <tr>
                      <td className="py-2 text-gray-700">Advertising</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(report.advertisingExpenses)}</td>
                    </tr>
                    <tr>
                      <td className="py-2 text-gray-700">Legal & Professional Fees</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(report.legalExpenses)}</td>
                    </tr>
                    <tr>
                      <td className="py-2 text-gray-700">Other Expenses</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(report.otherExpenses)}</td>
                    </tr>
                    <tr className="bg-gray-50 font-bold">
                      <td className="py-2 text-gray-900">Total Expenses</td>
                      <td className="py-2 text-right text-red-600">{formatCurrency(report.totalExpenses)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Net Income */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-gray-900">Net Rental Income</h3>
                  <span className={`text-2xl font-bold ${report.netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(report.netIncome)}
                  </span>
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  This is your taxable rental income for the selected period
                </p>
              </div>
            </div>

            {/* GST Section (for BAS) */}
            {report.reportType === 'QUARTERLY_BAS' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg shadow">
                <div className="px-6 py-4 border-b border-blue-200">
                  <h3 className="text-lg font-medium text-blue-900">GST Summary (BAS)</h3>
                </div>
                <div className="p-6">
                  <table className="w-full">
                    <tbody className="divide-y divide-blue-100">
                      <tr>
                        <td className="py-2 text-blue-900">GST Collected (Sales)</td>
                        <td className="py-2 text-right font-medium">{formatCurrency(report.gstCollected)}</td>
                      </tr>
                      <tr>
                        <td className="py-2 text-blue-900">GST Paid (Purchases)</td>
                        <td className="py-2 text-right font-medium">{formatCurrency(report.gstPaid)}</td>
                      </tr>
                      <tr className="bg-blue-100 font-bold">
                        <td className="py-2 text-blue-900">Net GST Payable/(Refundable)</td>
                        <td className="py-2 text-right">{formatCurrency(report.netGst)}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="mt-4 text-sm text-blue-700">
                    Note: Residential rental income is typically GST-free in Australia
                  </p>
                </div>
              </div>
            )}

            {/* ATO Information */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-900 mb-2">Important Tax Information</h4>
              <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                <li>This report is for informational purposes only</li>
                <li>Please consult with a registered tax agent or accountant</li>
                <li>Keep all receipts and invoices for deductible expenses</li>
                <li>Rental income must be declared in your tax return</li>
                <li>Deductions are only claimable for expenses directly related to earning rental income</li>
                <li>Visit ato.gov.au for official ATO guidance on rental property taxation</li>
              </ul>
            </div>
          </div>
        )}
    </div>
  );
};

export default TaxReportsPage;

